/**
 * NFeEditPage Component
 * 
 * Página de edição de rascunhos de NF-e modelo 55.
 * Permite editar rascunhos, adicionar/remover itens, selecionar cliente e finalizar venda.
 * 
 * Funcionalidades:
 * - Busca de produtos
 * - Adição de produtos ao rascunho
 * - Seleção de cliente
 * - Cálculo automático de totais
 * - Gerenciamento de itens (quantidade, preço, desconto)
 * - Salvar como rascunho
 * - Finalizar venda (emite NF-e)
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  X, Plus, Save, ArrowLeft, Trash2, Loader2, Package, 
  CheckCircle, Users, ChevronDown, ChevronUp, Building2, 
  UserCheck, Search, Table, Gift
} from 'lucide-react'
import { useToast } from '../../../../hooks/useToast'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import { Toast } from '../../../../components'
import ERPProductSearch from '../../../../components/ERP/ProductSearch'
import { 
  nfeService, 
  customerService,
  specialPricingTablesService,
  paymentReceiptMethodService,
  paymentTermService,
  customerCreditService
} from '../../../../services/erpApi'
import NFeStatusBadge from '../../../../components/ERP/NFe/NFeStatusBadge'
import './NFeEditPage.css'

function NFeEditPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { toast, showToast, hideToast } = useToast()

  // Estados principais
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [nfe, setNfe] = useState(null)
  const [items, setItems] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    customerId: '',
    observation: ''
  })

  // Estado do produto selecionado para adicionar
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [quantity, setQuantity] = useState('1,000')
  const [unitPrice, setUnitPrice] = useState('0,00')
  const [discountPercent, setDiscountPercent] = useState('0,00')

  // Estados dos modais
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  })
  const [customerModalOpen, setCustomerModalOpen] = useState(false)
  const [tableModalOpen, setTableModalOpen] = useState(false)
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  
  // Estados do modal de cliente
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [filteredCustomers, setFilteredCustomers] = useState([])
  const [loadingCustomers, setLoadingCustomers] = useState(false)
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1)
  
  // Estados do modal de pagamento
  const [paymentMethods, setPaymentMethods] = useState([])
  const [paymentTerms, setPaymentTerms] = useState([])
  const [loadingPayments, setLoadingPayments] = useState(false)
  const [selectedPayments, setSelectedPayments] = useState([])
  const [paymentError, setPaymentError] = useState('')
  const [showTermModal, setShowTermModal] = useState(false)
  const [savingTerm, setSavingTerm] = useState(false)
  const [termFormData, setTermFormData] = useState({
    name: '',
    code: '',
    installments: 2,
    daysUntilFirst: 30,
    daysBetweenInstallments: 30,
    active: true
  })
  const [termErrors, setTermErrors] = useState({})
  
  // Estados para bônus do cliente
  const [customerBonusBalance, setCustomerBonusBalance] = useState(null)
  
  // Estado para controlar se a seção de Informações Adicionais está expandida
  const [isAdditionalInfoExpanded, setIsAdditionalInfoExpanded] = useState(false)
  
  // Estados para tabela de preços
  const [tableSearch, setTableSearch] = useState('')
  const [selectedTable, setSelectedTable] = useState('Tabela Geral Medik')
  const [selectedTableId, setSelectedTableId] = useState(null)
  const [pricingTables, setPricingTables] = useState([])
  const [filteredTables, setFilteredTables] = useState([])
  const [loadingTables, setLoadingTables] = useState(false)
  const [selectedTableIndex, setSelectedTableIndex] = useState(-1)
  const [productInTable, setProductInTable] = useState({}) // { productId: boolean }

  // Carregar saldo de bônus do cliente
  const loadCustomerBonusBalance = useCallback(async () => {
    if (!formData.customerId) return
    
    try {
      const response = await customerCreditService.getAvailableBalance(formData.customerId)
      if (response.success) {
        setCustomerBonusBalance(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar saldo de bônus:', error)
      setCustomerBonusBalance(null)
    }
  }, [formData.customerId])

  // Carregar dados iniciais
  useEffect(() => {
    if (id) {
      loadNFe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Carregar formas de pagamento e saldo de bônus quando o modal abrir
  useEffect(() => {
    if (paymentModalOpen) {
      loadPaymentData()
      if (formData.customerId) {
        loadCustomerBonusBalance()
      }
    }
  }, [paymentModalOpen, formData.customerId, loadCustomerBonusBalance])
  
  // Carregar saldo de bônus quando cliente mudar
  useEffect(() => {
    if (formData.customerId && paymentModalOpen) {
      loadCustomerBonusBalance()
    } else {
      setCustomerBonusBalance(null)
    }
  }, [formData.customerId, paymentModalOpen, loadCustomerBonusBalance])

  // Carregar tabelas de preços
  const loadPricingTables = useCallback(async (customerId = null) => {
    setLoadingTables(true)
    try {
      let response
      if (customerId) {
        // Buscar tabelas específicas do cliente
        response = await specialPricingTablesService.getByClientId(customerId)
      } else {
        // Buscar todas as tabelas
        response = await specialPricingTablesService.getAll()
      }
      
      if (response.success) {
        const tables = response.data || []
        // Adicionar "Tabela Geral Medik" como primeira opção
        const allTables = [
          { id: null, name: 'Tabela Geral Medik', is_default: true },
          ...tables
        ]
        setPricingTables(allTables)
        
        // Se não houver tabela selecionada, selecionar a padrão
        if (!selectedTableId && selectedTable === 'Tabela Geral Medik') {
          setSelectedTableId(null)
        }
      }
    } catch (error) {
      console.error('Erro ao carregar tabelas de preços:', error)
      // Em caso de erro, usar apenas a tabela padrão
      setPricingTables([{ id: null, name: 'Tabela Geral Medik', is_default: true }])
      setSelectedTable('Tabela Geral Medik')
      setSelectedTableId(null)
    } finally {
      setLoadingTables(false)
    }
  }, [selectedTableId, selectedTable])

  // Carregar tabelas quando o modal de tabela abrir
  useEffect(() => {
    if (tableModalOpen) {
      if (formData.customerId) {
        loadPricingTables(parseInt(formData.customerId))
      } else {
        loadPricingTables()
      }
      setTableSearch('')
      setSelectedTableIndex(-1)
    }
  }, [tableModalOpen, formData.customerId, loadPricingTables])

  // Filtrar tabelas baseado na busca
  useEffect(() => {
    if (!tableSearch.trim()) {
      setFilteredTables(pricingTables.slice(0, 20))
      return
    }

    const term = tableSearch.toLowerCase().trim()
    const filtered = pricingTables.filter(table => {
      return table.name?.toLowerCase().includes(term)
    }).slice(0, 20)

    setFilteredTables(filtered)
    setSelectedTableIndex(-1)
  }, [tableSearch, pricingTables])

  // Carregar tabelas quando cliente for selecionado
  useEffect(() => {
    if (formData.customerId) {
      loadPricingTables(parseInt(formData.customerId))
    } else {
      loadPricingTables()
    }
  }, [formData.customerId, loadPricingTables])

  // Verificar se produto está na tabela
  const checkProductInTable = useCallback(async (productId) => {
    if (!selectedTableId || !formData.customerId) {
      setProductInTable(prev => ({ ...prev, [productId]: false }))
      return false
    }

    try {
      // Buscar itens da tabela para verificar se o produto está nela
      const response = await specialPricingTablesService.getItems(selectedTableId)
      if (response.success) {
        const items = response.data || []
        const isInTable = items.some(item => item.product_id === productId && item.active)
        setProductInTable(prev => ({ ...prev, [productId]: isInTable }))
        return isInTable
      }
    } catch (error) {
      console.error('Erro ao verificar produto na tabela:', error)
    }
    
    setProductInTable(prev => ({ ...prev, [productId]: false }))
    return false
  }, [selectedTableId, formData.customerId])

  // Verificar múltiplos produtos na tabela
  const checkProductsInTable = useCallback(async (productIds) => {
    if (!selectedTableId || !formData.customerId || productIds.length === 0) {
      return
    }

    try {
      const response = await specialPricingTablesService.getItems(selectedTableId)
      if (response.success) {
        const tableItems = response.data || []
        const inTableMap = {}
        productIds.forEach(productId => {
          inTableMap[productId] = tableItems.some(item => item.product_id === productId && item.active)
        })
        setProductInTable(prev => ({ ...prev, ...inTableMap }))
      }
    } catch (error) {
      console.error('Erro ao verificar produtos na tabela:', error)
    }
  }, [selectedTableId, formData.customerId])

  // Selecionar tabela
  const handleSelectTable = async (table) => {
    setSelectedTable(table.name)
    setSelectedTableId(table.id)
    setTableModalOpen(false)
    
    // Recalcular preços imediatamente quando selecionar uma tabela
    if (formData.customerId && items.length > 0) {
      try {
        let updatedItems
        
        if (table.id) {
          // Tabela específica: buscar preços da API usando o tableId
          updatedItems = await Promise.all(
            items.map(async (item) => {
              try {
                const priceResponse = await specialPricingTablesService.calculatePrice(
                  parseInt(formData.customerId),
                  item.product_id,
                  table.id
                )
                if (priceResponse.success && priceResponse.data?.price !== null && priceResponse.data?.price !== undefined) {
                  const newPrice = parseFloat(priceResponse.data.price)
                  const discountAmount = (newPrice * item.quantity * item.discount_percent) / 100
                  const totalPrice = (newPrice * item.quantity) - discountAmount
                  
                  // Verificar se produto está na tabela
                  const tableItemsResponse = await specialPricingTablesService.getItems(table.id)
                  const isInTable = tableItemsResponse.success && 
                    (tableItemsResponse.data || []).some(ti => ti.product_id === item.product_id && ti.active)
                  
                  return {
                    ...item,
                    unit_price: newPrice,
                    discount_amount: discountAmount,
                    total_price: totalPrice,
                    // Se está na tabela, zerar desconto
                    discount_percent: isInTable ? 0 : item.discount_percent
                  }
                } else {
                  const defaultPrice = item.product?.price || item.unit_price
                  const discountAmount = (defaultPrice * item.quantity * item.discount_percent) / 100
                  const totalPrice = (defaultPrice * item.quantity) - discountAmount
                  return {
                    ...item,
                    unit_price: defaultPrice,
                    discount_amount: discountAmount,
                    total_price: totalPrice
                  }
                }
              } catch (error) {
                console.error(`Erro ao buscar preço para produto ${item.product_id}:`, error)
                return item
              }
            })
          )
          showToast('Preços atualizados conforme a tabela selecionada', 'success')
        } else {
          // Tabela padrão: usar preços base dos produtos
          updatedItems = items.map(item => {
            const defaultPrice = item.product?.price || item.unit_price
            const discountAmount = (defaultPrice * item.quantity * item.discount_percent) / 100
            const totalPrice = (defaultPrice * item.quantity) - discountAmount
            return {
              ...item,
              unit_price: defaultPrice,
              discount_amount: discountAmount,
              total_price: totalPrice
            }
          })
          showToast('Preços atualizados para tabela padrão', 'success')
        }
        
        // Atualizar itens e recarregar NF-e
        setItems(updatedItems)
        // Salvar alterações no backend
        for (const item of updatedItems) {
          try {
            await nfeService.atualizarItem(id, item.id, {
              unitPrice: item.unit_price,
              discountPercent: item.discount_percent,
              quantity: item.quantity
            })
          } catch (error) {
            console.error(`Erro ao atualizar item ${item.id}:`, error)
          }
        }
        await loadNFe()
      } catch (error) {
        console.error('Erro ao atualizar preços:', error)
        showToast('Erro ao atualizar preços da tabela', 'error')
      }
    }
  }

  // Verificar produtos na tabela quando tabela ou itens mudarem
  useEffect(() => {
    if (selectedTableId && formData.customerId && items.length > 0) {
      const productIds = items.map(item => item.product_id).filter(Boolean)
      if (productIds.length > 0) {
        checkProductsInTable(productIds)
      }
    } else if (!selectedTableId) {
      // Se não há tabela selecionada, limpar estado
      setProductInTable({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableId, formData.customerId, items.length, checkProductsInTable])

  // Buscar clientes quando o modal abrir ou quando o termo de busca mudar
  useEffect(() => {
    if (!customerModalOpen) {
      // Limpar estado quando o modal fechar
      setFilteredCustomers([])
      setCustomerSearchTerm('')
      return
    }

    // Debounce: aguardar 300ms após o usuário parar de digitar (apenas quando há busca)
    const timeoutId = setTimeout(async () => {
      setLoadingCustomers(true)
      try {
        const filters = {
          status: 'active'
        }

        // Se há termo de busca, usar busca no backend (busca em TODOS os 7791 clientes)
        if (customerSearchTerm.trim()) {
          filters.search = customerSearchTerm.trim()
          // Não usar limit quando há busca, para retornar todos os resultados da busca
        } else {
          // Sem busca: carregar primeiros 1000 para exibição inicial
          filters.limit = 1000
        }

        const response = await customerService.getAll(filters)
        
        if (response.success) {
          const customersData = response.data || []
          console.log('[INVESTIGAÇÃO] Clientes retornados da API:', customersData.length)
          console.log('[INVESTIGAÇÃO] Com busca:', !!customerSearchTerm.trim())
          console.log('[INVESTIGAÇÃO] Termo de busca:', customerSearchTerm.trim() || '(nenhum)')
          console.log('[INVESTIGAÇÃO] Paginação:', response.pagination)
          
          setFilteredCustomers(customersData)
          setSelectedCustomerIndex(-1)
        } else {
          console.error('[INVESTIGAÇÃO] Resposta da API não foi bem-sucedida:', response)
          setFilteredCustomers([])
        }
      } catch (error) {
        console.error('[INVESTIGAÇÃO] Erro ao buscar clientes:', error)
        setFilteredCustomers([])
      } finally {
        setLoadingCustomers(false)
      }
    }, customerSearchTerm.trim() ? 300 : 0) // Sem debounce quando não há busca (carregamento inicial)

    return () => clearTimeout(timeoutId)
  }, [customerModalOpen, customerSearchTerm])

  const loadPaymentData = async () => {
    setLoadingPayments(true)
    try {
      const [methodsRes, termsRes] = await Promise.all([
        paymentReceiptMethodService.getForSales({ active: true }),
        paymentTermService.getAll()
      ])

      if (methodsRes.success) {
        setPaymentMethods(methodsRes.data || [])
      }

      if (termsRes.success) {
        setPaymentTerms(termsRes.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar dados de pagamento:', error)
    } finally {
      setLoadingPayments(false)
    }
  }

  const loadNFe = useCallback(async () => {
    setLoading(true)
    try {
      const response = await nfeService.getById(id)
      if (response.success && response.data) {
        const nfeData = response.data
        setNfe(nfeData)
        setFormData({
          customerId: nfeData.customer_id || '',
          observation: nfeData.observation || ''
        })
        
        // Carregar dados do cliente se houver
        if (nfeData.customer_id) {
          try {
            const customerResponse = await customerService.getById(nfeData.customer_id)
            if (customerResponse.success && customerResponse.data) {
              setSelectedCustomer(customerResponse.data)
            }
          } catch (error) {
            console.error('Erro ao carregar cliente:', error)
          }
        }
        
        // Carregar itens
        if (nfeData.items && nfeData.items.length > 0) {
          const formattedItems = nfeData.items.map(item => ({
            id: item.id,
            product_id: item.product_id,
            is_supplemental: item.is_supplemental === true || item.item_source === 'sale_supplemental',
            product: {
              id: item.product_id,
              name: item.product_name || `Produto #${item.product_id}`,
              sku: item.sku || '',
              price: item.unit_price
            },
            quantity: parseFloat(item.quantity || 0),
            unit_price: parseFloat(item.unit_price || 0),
            discount_percent: parseFloat(item.discount_percent || 0),
            discount_amount: parseFloat(item.discount_amount || 0),
            total_price: parseFloat(item.total_price || 0)
          }))
          setItems(formattedItems)
          // A verificação de produtos na tabela será feita pelo useEffect quando os itens mudarem
        }
      } else {
        showToast('NF-e não encontrada', 'error')
        navigate('/erp/vendas/nfe')
      }
    } catch (error) {
      console.error('Erro ao carregar NF-e:', error)
      showToast('Erro ao carregar NF-e', 'error')
      navigate('/erp/vendas/nfe')
    } finally {
      setLoading(false)
    }
  }, [id, navigate, showToast])

  // Selecionar produto do ERPProductSearch
  const handleSelectProduct = useCallback(async (product) => {
    setSelectedProduct(product)
    
    // Verificar se há cliente selecionado e tabela para aplicar preço de tabela especial
    let priceValue = product.price ? parseFloat(product.price) : 0
    let isInTable = false
    
    if (formData.customerId && product.id) {
      try {
        // Se houver tabela selecionada, usar ela; senão, usar a lógica padrão
        const priceResponse = await specialPricingTablesService.calculatePrice(
          formData.customerId,
          product.id,
          selectedTableId || undefined
        )
        
        if (priceResponse.success && priceResponse.data?.price !== null && priceResponse.data?.price !== undefined) {
          priceValue = parseFloat(priceResponse.data.price)
          
          // Verificar se o produto está na tabela (apenas se houver tabela selecionada)
          if (selectedTableId) {
            isInTable = await checkProductInTable(product.id)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar preço da tabela especial:', error)
        // Em caso de erro, usar preço padrão do produto
      }
    }
    
    setUnitPrice(priceValue > 0 ? priceValue.toFixed(2).replace('.', ',') : '0,00')
    setQuantity('1,000')
    // Se produto está na tabela, não permitir desconto
    setDiscountPercent(isInTable ? '0,00' : '0,00')
    setSelectedProduct(product)
    
    setTimeout(() => {
      const qtyInput = document.getElementById('quantity-input')
      if (qtyInput) {
        qtyInput.focus()
        qtyInput.select()
      }
    }, 100)
  }, [formData.customerId, selectedTableId, checkProductInTable])

  // Adicionar item ao rascunho
  const handleAddItem = useCallback(async () => {
    if (!selectedProduct) return

    // Verificar se produto está na tabela e bloquear desconto
    const isInTable = productInTable[selectedProduct.id] || false
    if (isInTable) {
      const discount = parseFloat(discountPercent.replace(',', '.')) || 0
      if (discount > 0) {
        showToast('Produtos na tabela de preços não podem ter desconto', 'warning')
        setDiscountPercent('0,00')
        return
      }
    }

    // Converter do formato brasileiro: vírgula = decimal
    // "1,000" = 1.0, "1,5" = 1.5, "1.000" = 1000
    // Se tem vírgula, substituir por ponto (decimal)
    // Se tem ponto sem vírgula, pode ser milhar ou decimal
    let qty = 0
    if (quantity.includes(',')) {
      // Tem vírgula = decimal brasileiro
      qty = parseFloat(quantity.replace(',', '.')) || 1
    } else if (quantity.includes('.')) {
      // Tem ponto, verificar se é milhar ou decimal
      const parts = quantity.split('.')
      if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3)) {
        // É milhar (ex: "1.000" ou "1.000.000")
        qty = parseFloat(quantity.replace(/\./g, '')) || 1
      } else {
        // É decimal (ex: "1.5")
        qty = parseFloat(quantity) || 1
      }
    } else {
      qty = parseFloat(quantity) || 1
    }
    
    const price = parseFloat(unitPrice.replace(/\./g, '').replace(',', '.')) || 0
    const discount = isInTable ? 0 : (parseFloat(discountPercent.replace(',', '.')) || 0)
    
    if (qty <= 0) {
      showToast('Quantidade deve ser maior que zero', 'warning')
      return
    }

    if (price < 0) {
      showToast('Preço não pode ser negativo', 'warning')
      return
    }

    const discountAmount = (price * qty * discount) / 100

    try {
      const response = await nfeService.adicionarItem(id, {
        productId: selectedProduct.id,
        quantity: qty,
        unitPrice: price,
        discountPercent: discount,
        discountAmount: discountAmount
      })

      if (response.success) {
        // Recarregar itens
        await loadNFe()
        
        // Limpar campos
        setSelectedProduct(null)
        setQuantity('1,000')
        setUnitPrice('0,00')
        setDiscountPercent('0,00')
        
        showToast('Item adicionado com sucesso', 'success')
      } else {
        showToast(response.message || 'Erro ao adicionar item', 'error')
      }
    } catch (error) {
      console.error('Erro ao adicionar item:', error)
      showToast('Erro ao adicionar item', 'error')
    }
  }, [id, selectedProduct, quantity, unitPrice, discountPercent, productInTable, loadNFe, showToast])

  // Remover item
  const handleRemoveItem = async (itemId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Remover Item',
      message: 'Deseja realmente remover este item?',
      onConfirm: async () => {
        try {
          const response = await nfeService.removerItem(id, itemId)
          if (response.success) {
            // Remover item do estado local imediatamente para feedback visual
            setItems(prevItems => prevItems.filter(item => item.id !== itemId))
            
            // Recarregar dados do servidor para garantir sincronização
            await loadNFe()
            
            showToast('Item removido com sucesso', 'success')
          } else {
            // Se falhou, recarregar para restaurar estado
            await loadNFe()
            showToast(response.message || 'Erro ao remover item', 'error')
          }
        } catch (error) {
          console.error('Erro ao remover item:', error)
          // Recarregar para restaurar estado em caso de erro
          await loadNFe()
          showToast('Erro ao remover item', 'error')
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        }
      }
    })
  }

  // Atualizar item
  const handleUpdateItem = async (itemId, field, value) => {
    const item = items.find(i => i.id === itemId)
    if (!item) return

    // Verificar se produto está na tabela antes de permitir desconto
    if (field === 'discount_percent') {
      const isInTable = productInTable[item.product_id] || false
      if (isInTable) {
        showToast('Produtos na tabela de preços não podem ter desconto', 'warning')
        return
      }
    }

    let updateData = {}
    
    if (field === 'quantity') {
      const qty = parseFloat(value.replace(',', '.')) || 0
      updateData.quantity = qty
    } else if (field === 'discount_percent') {
      const discount = parseFloat(value.replace(',', '.')) || 0
      updateData.discountPercent = discount
    }
    // Preço unitário não pode ser alterado manualmente

    try {
      const response = await nfeService.atualizarItem(id, itemId, updateData)
      if (response.success) {
        await loadNFe()
        showToast('Item atualizado com sucesso', 'success')
      } else {
        showToast(response.message || 'Erro ao atualizar item', 'error')
      }
    } catch (error) {
      console.error('Erro ao atualizar item:', error)
      showToast('Erro ao atualizar item', 'error')
    }
  }

  // Calcular totais
  const calculateTotals = useCallback(() => {
    const totalAmount = items.reduce((sum, item) => {
      const price = parseFloat(item.unit_price) || 0
      const qty = parseFloat(item.quantity) || 0
      const itemTotal = price * qty
      return sum + (isNaN(itemTotal) ? 0 : itemTotal)
    }, 0)
    
    const discountAmount = items.reduce((sum, item) => {
      const discount = parseFloat(item.discount_amount) || 0
      return sum + (isNaN(discount) ? 0 : discount)
    }, 0)
    
    const finalAmount = totalAmount - discountAmount

    return {
      totalAmount: isNaN(totalAmount) ? 0 : Number(totalAmount),
      discountAmount: isNaN(discountAmount) ? 0 : Number(discountAmount),
      finalAmount: isNaN(finalAmount) ? 0 : Number(finalAmount)
    }
  }, [items])

  const totals = calculateTotals()

  // Formatar moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  // Selecionar cliente do modal
  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer)
    const newFormData = { ...formData, customerId: customer.id }
    setFormData(newFormData)
    setCustomerModalOpen(false)
    
    // Se há produto selecionado, atualizar preço com base na tabela especial do cliente
    if (selectedProduct && selectedProduct.id) {
      try {
        const priceResponse = await specialPricingTablesService.calculatePrice(
          customer.id,
          selectedProduct.id
        )
        
        if (priceResponse.success && priceResponse.data?.price !== null && priceResponse.data?.price !== undefined) {
          const priceValue = parseFloat(priceResponse.data.price)
          setUnitPrice(priceValue > 0 ? priceValue.toFixed(2).replace('.', ',') : '0,00')
        }
      } catch (error) {
        console.error('Erro ao buscar preço da tabela especial:', error)
        // Em caso de erro, manter preço atual
      }
    }
    
    // Salvar automaticamente o cliente no rascunho
    try {
      const response = await nfeService.salvarRascunho(id, {
        customerId: customer.id,
        observation: formData.observation || null
      })
      
      if (response.success) {
        showToast('Cliente selecionado e salvo', 'success')
      }
    } catch (error) {
      console.error('Erro ao salvar cliente:', error)
      // Não mostrar erro para o usuário, apenas logar
    }
  }

  // Salvar rascunho
  const handleSave = async () => {
    if (items.length === 0) {
      showToast('Adicione pelo menos um item à NF-e', 'warning')
      return
    }

    setSaving(true)
    try {
      const response = await nfeService.salvarRascunho(id, {
        customerId: formData.customerId || null,
        observation: formData.observation || null
      })

      if (response.success) {
        showToast('Rascunho salvo com sucesso', 'success')
        navigate('/erp/vendas/nfe')
      } else {
        showToast(response.message || 'Erro ao salvar rascunho', 'error')
      }
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error)
      showToast('Erro ao salvar rascunho', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Finalizar venda
  const handleFinalize = async () => {
    if (!formData.customerId) {
      showToast('Selecione um cliente', 'warning')
      return
    }

    if (items.length === 0) {
      showToast('Adicione pelo menos um item à NF-e', 'warning')
      return
    }

    // Estoque é baixado no caixa/venda — NF-e não valida nem movimenta estoque
    try {
      await nfeService.salvarRascunho(id, {
        customerId: formData.customerId,
        observation: formData.observation || null
      })
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error)
      showToast('Erro ao salvar rascunho', 'error')
      return
    }

    setSelectedPayments([])
    setPaymentError('')
    setPaymentModalOpen(true)
  }

  // Funções do modal de pagamento
  const handleAddPayment = (method) => {
    const isCrediario = method.name?.toLowerCase().includes('crediário') || 
                       method.name?.toLowerCase().includes('crediario') ||
                       method.code === 'CREDIARIO' ||
                       method.payment_type === 'CREDIÁRIO' ||
                       method.payment_type === 'CREDIARIO'
    
    const isBonus = method === 'BONUS' || method.name === 'Bônus'
    
    const newPayment = {
      id: Date.now(),
      paymentReceiptMethodId: isBonus ? null : method.id, // Bônus não tem ID de método
      methodName: isBonus ? 'Bônus' : method.name,
      amount: 0,
      isCrediario: isCrediario,
      isBonus: isBonus,
      installments: isCrediario ? [] : null,
      paymentTermId: null
    }

    setSelectedPayments([...selectedPayments, newPayment])
  }
  
  // Adicionar forma de pagamento Bônus manualmente
  const handleAddBonusPayment = () => {
    const newPayment = {
      id: Date.now(),
      paymentReceiptMethodId: null,
      methodName: 'Bônus',
      amount: 0,
      isCrediario: false,
      isBonus: true,
      installments: null,
      paymentTermId: null
    }
    setSelectedPayments([...selectedPayments, newPayment])
  }

  const handleRemovePayment = (paymentId) => {
    setSelectedPayments(selectedPayments.filter(p => p.id !== paymentId))
  }

  const handleUpdatePaymentAmount = (paymentId, amount) => {
    setSelectedPayments(selectedPayments.map(p => {
      if (p.id === paymentId) {
        const newAmount = parseFloat(amount) || 0
        
        // Validar bônus: não pode exceder o saldo disponível
        if (p.isBonus && customerBonusBalance) {
          const availableBalance = parseFloat(customerBonusBalance.availableBalance || 0)
          if (newAmount > availableBalance) {
            setPaymentError(`O valor do bônus (${formatCurrency(newAmount)}) não pode exceder o saldo disponível (${formatCurrency(availableBalance)})`)
            return p
          }
          setPaymentError('')
        }
        
        if (p.isCrediario && p.paymentTermId) {
          const term = paymentTerms.find(t => t.id === p.paymentTermId)
          if (term) {
            const installments = calculateInstallments(newAmount, term)
            return { ...p, amount: newAmount, installments: installments }
          }
        }
        return { ...p, amount: newAmount }
      }
      return p
    }))
  }

  const handleUpdatePaymentTerm = (paymentId, termId) => {
    const term = paymentTerms.find(t => t.id === parseInt(termId))
    if (!term) return

    const payment = selectedPayments.find(p => p.id === paymentId)
    if (!payment) return

    const installments = calculateInstallments(payment.amount, term)

    setSelectedPayments(selectedPayments.map(p => {
      if (p.id === paymentId) {
        return {
          ...p,
          paymentTermId: parseInt(termId),
          installments: installments
        }
      }
      return p
    }))
  }

  const calculateInstallments = (amount, term) => {
    const installments = []
    const installmentAmount = amount / term.installments
    const baseDate = new Date()
    baseDate.setDate(baseDate.getDate() + (term.days_until_first || 0))

    for (let i = 0; i < term.installments; i++) {
      const dueDate = new Date(baseDate)
      dueDate.setDate(dueDate.getDate() + (i * (term.days_between_installments || 30)))
      
      installments.push({
        installmentNumber: i + 1,
        dueDate: dueDate.toISOString().split('T')[0],
        amount: installmentAmount
      })
    }

    return installments
  }

  const handleUpdateInstallmentDate = (paymentId, installmentNumber, date) => {
    setSelectedPayments(selectedPayments.map(p => {
      if (p.id === paymentId && p.installments) {
        return {
          ...p,
          installments: p.installments.map(inst => {
            if (inst.installmentNumber === installmentNumber) {
              return { ...inst, dueDate: date }
            }
            return inst
          })
        }
      }
      return p
    }))
  }

  const calculatePaymentTotal = () => {
    return selectedPayments.reduce((sum, p) => sum + (p.amount || 0), 0)
  }

  const handleCreateTerm = async () => {
    setTermErrors({})
    
    if (!termFormData.name || termFormData.name.trim() === '') {
      setTermErrors({ name: 'Nome é obrigatório' })
      return
    }
    
    if (!termFormData.installments || termFormData.installments < 1) {
      setTermErrors({ installments: 'Número de parcelas deve ser maior que zero' })
      return
    }
    
    if (termFormData.daysUntilFirst < 0) {
      setTermErrors({ daysUntilFirst: 'Dias até primeira parcela não pode ser negativo' })
      return
    }
    
    if (termFormData.daysBetweenInstallments < 0) {
      setTermErrors({ daysBetweenInstallments: 'Dias entre parcelas não pode ser negativo' })
      return
    }
    
    setSavingTerm(true)
    try {
      const response = await paymentTermService.create({
        name: termFormData.name.trim(),
        code: termFormData.code.trim() || null,
        installments: parseInt(termFormData.installments),
        daysUntilFirst: parseInt(termFormData.daysUntilFirst),
        daysBetweenInstallments: parseInt(termFormData.daysBetweenInstallments),
        active: termFormData.active
      })
      
      if (response.success) {
        await loadPaymentData()
        setShowTermModal(false)
        setTermFormData({
          name: '',
          code: '',
          installments: 2,
          daysUntilFirst: 30,
          daysBetweenInstallments: 30,
          active: true
        })
        setTermErrors({})
        
        if (response.data) {
          const crediarioPayment = selectedPayments.find(p => p.isCrediario && !p.paymentTermId)
          if (crediarioPayment) {
            handleUpdatePaymentTerm(crediarioPayment.id, response.data.id)
          }
        }
      } else {
        setTermErrors({ submit: response.message || 'Erro ao cadastrar prazo' })
      }
    } catch (error) {
      console.error('Erro ao cadastrar prazo:', error)
      setTermErrors({ submit: error.response?.data?.message || 'Erro ao cadastrar prazo' })
    } finally {
      setSavingTerm(false)
    }
  }

  // Confirmar pagamentos e finalizar
  const handleConfirmPayments = async () => {
    const total = calculatePaymentTotal()
    const difference = Math.abs(total - totals.finalAmount)

    if (difference > 0.01) {
      setPaymentError(`O total dos pagamentos (${formatCurrency(total)}) deve ser igual ao valor da venda (${formatCurrency(totals.finalAmount)})`)
      return
    }

    // Validar crediários
    for (const payment of selectedPayments) {
      if (payment.isCrediario) {
        if (!payment.paymentTermId) {
          setPaymentError('Selecione um prazo de pagamento para o crediário')
          return
        }
        if (!payment.installments || payment.installments.length === 0) {
          setPaymentError('Configure as parcelas do crediário')
          return
        }
      }
      
      // Validar bônus
      if (payment.isBonus) {
        if (!formData.customerId) {
          setPaymentError('Selecione um cliente para usar bônus')
          return
        }
        if (!customerBonusBalance || parseFloat(customerBonusBalance.availableBalance || 0) <= 0) {
          setPaymentError('Cliente não possui saldo de bônus disponível')
          return
        }
        const bonusAmount = parseFloat(payment.amount || 0)
        const availableBalance = parseFloat(customerBonusBalance.availableBalance || 0)
        if (bonusAmount <= 0) {
          setPaymentError('Informe o valor do bônus a ser utilizado')
          return
        }
        if (bonusAmount > availableBalance) {
          setPaymentError(`O valor do bônus (${formatCurrency(bonusAmount)}) não pode exceder o saldo disponível (${formatCurrency(availableBalance)})`)
          return
        }
      }
    }

    setPaymentError('')
    
    // Separar pagamentos normais e bônus
    const bonusPayments = selectedPayments.filter(p => p.isBonus)
    const normalPayments = selectedPayments.filter(p => !p.isBonus)
    
    // Converter pagamentos normais para formato esperado pelo backend
    // IMPORTANTE: Para crediário, manter a estrutura completa com isCrediario e installments
    const payments = normalPayments.map(payment => {
      if (payment.isCrediario && payment.installments && payment.installments.length > 0) {
        // Para crediário, enviar o pagamento completo com todas as parcelas
        // O backend precisa dessa estrutura para criar as contas a receber
        return {
          paymentReceiptMethodId: payment.paymentReceiptMethodId,
          paymentTermId: payment.paymentTermId,
          amount: payment.amount, // Valor total do crediário
          isCrediario: true, // Marcar explicitamente como crediário
          installments: payment.installments.map(inst => ({
            installmentNumber: inst.installmentNumber,
            dueDate: inst.dueDate,
            amount: inst.amount
          })),
          status: 'pending',
          observation: null
        }
      } else {
        // Pagamento à vista
        return {
          paymentReceiptMethodId: payment.paymentReceiptMethodId,
          paymentTermId: null,
          installmentNumber: 1,
          dueDate: new Date().toISOString().split('T')[0],
          amount: payment.amount,
          status: 'paid',
          observation: null
        }
      }
    })
    
    // Armazenar informações de bônus para usar depois
    const bonusAmount = bonusPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)

    setPaymentModalOpen(false)
    
    // Não alterar status aqui - o status será alterado apenas quando finalizar a venda
    // Isso garante que se o usuário cancelar, o status não muda
    
    setConfirmModal({
      isOpen: true,
      title: 'Finalizar Venda',
      message: 'Deseja finalizar a venda e emitir a NF-e? Esta ação não pode ser desfeita.',
      onConfirm: async () => {
        setSaving(true)
        try {
          // Se houver bônus, usar os créditos antes de finalizar
          if (bonusAmount > 0 && formData.customerId) {
            try {
              // Buscar créditos disponíveis do cliente
              const creditsResponse = await customerCreditService.getByCustomer(formData.customerId, { status: 'approved' })
              if (creditsResponse.success && creditsResponse.data) {
                const availableCredits = creditsResponse.data.filter(credit => {
                  const available = parseFloat(credit.amount) - parseFloat(credit.used_amount || 0)
                  return available > 0
                })
                
                let remainingAmount = bonusAmount
                
                // Usar créditos proporcionalmente até atingir o valor necessário
                for (const credit of availableCredits) {
                  if (remainingAmount <= 0) break
                  
                  const available = parseFloat(credit.amount) - parseFloat(credit.used_amount || 0)
                  const amountToUse = Math.min(remainingAmount, available)
                  
                  if (amountToUse > 0) {
                    await customerCreditService.use(credit.id, amountToUse)
                    remainingAmount -= amountToUse
                  }
                }
                
                if (remainingAmount > 0.01) {
                  showToast(`Atenção: Foi possível usar apenas ${formatCurrency(bonusAmount - remainingAmount)} de bônus. Saldo insuficiente.`, 'warning')
                }
              }
            } catch (error) {
              console.error('Erro ao usar bônus:', error)
              showToast('Erro ao usar bônus. A venda será finalizada sem usar o bônus.', 'error')
            }
          }
          
          const response = await nfeService.finalizarVenda(id, {
            customerId: formData.customerId,
            observation: formData.observation || null,
            payments: payments,
            bonusAmount: bonusAmount > 0 ? bonusAmount : null
          })

          if (response.success) {
            showToast('Venda finalizada e NF-e emitida com sucesso!', 'success')
            navigate('/erp/vendas/nfe')
          } else {
            showToast(response.message || 'Erro ao finalizar venda', 'error')
          }
        } catch (error) {
          console.error('Erro ao finalizar venda:', error)
          showToast(error.response?.data?.message || 'Erro ao finalizar venda', 'error')
        } finally {
          setSaving(false)
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        }
      }
    })
  }

  if (loading) {
    return (
      <div className="nfe-edit-page loading">
        <Loader2 className="spinner" size={32} />
        <p>Carregando NF-e...</p>
      </div>
    )
  }

  if (!nfe) {
    return null
  }

  const isReadOnly = nfe?.status && nfe.status !== 'draft'
  const subtotal = totals.totalAmount
  const totalDiscount = totals.discountAmount
  const finalTotal = totals.finalAmount

  return (
    <div className="nfe-edit-page">
      {/* Header Bar */}
      <div className="nfe-edit-header">
        <div className="nfe-edit-header-left">
          <button
            className="nfe-edit-btn-back" 
            onClick={() => navigate('/erp/vendas/nfe')}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="nfe-edit-title">
              Editar <span className="nfe-edit-title-accent">NF-e</span> #{nfe.number}
            </h1>
            <p className="nfe-edit-subtitle">Emissão de Nota Fiscal Eletrônica</p>
          </div>
        </div>
        <div className="nfe-edit-header-right">
          <NFeStatusBadge status={nfe.status} />
        </div>
      </div>

      {/* Card de Dados do Cliente e Tabela - Largura Total */}
      <div style={{ width: '100%', marginBottom: '1.5rem' }}>
        <div className="nfe-edit-card" style={{ width: '100%' }}>
          <div className="nfe-edit-card-bg" />
          <h3 className="nfe-edit-card-title">
            <UserCheck size={16} className="nfe-edit-card-icon" />
            Dados do Cliente
          </h3>

          <div className="nfe-edit-card-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {/* Seleção de Cliente */}
            <div className="nfe-edit-field">
              <label className="nfe-edit-label">Cliente</label>
              <div className="nfe-edit-input-wrapper">
                <Building2 size={18} className="nfe-edit-input-icon" />
                {selectedCustomer ? (
                  <input
                    type="text"
                    className="nfe-edit-input"
                    value={selectedCustomer.name || ''}
                    readOnly
                    onClick={() => !isReadOnly && setCustomerModalOpen(true)}
                    style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                  />
                ) : (
                  <input
                    type="text"
                    className="nfe-edit-input"
                    placeholder="Selecione um cliente..."
                    readOnly
                    onClick={() => !isReadOnly && setCustomerModalOpen(true)}
                    style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                  />
                )}
              </div>
              {selectedCustomer && (
                <p className="nfe-edit-field-hint">
                  {selectedCustomer.cpf_cnpj && (
                    selectedCustomer.cpf_cnpj.length === 11 
                      ? `CPF: ${selectedCustomer.cpf_cnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}`
                      : `CNPJ: ${selectedCustomer.cpf_cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}`
                  )}
                </p>
              )}
            </div>

            {/* Seletor de Tabela de Preços */}
            {selectedCustomer && (
              <div className="nfe-edit-field">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <label className="nfe-edit-label">Tabela de Preços</label>
                  {selectedTable !== 'Tabela Geral Medik' && (
                    <span style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem', 
                      background: '#d1fae5', 
                      color: '#059669', 
                      borderRadius: '8px',
                      fontWeight: '500'
                    }}>
                      Customizada
                    </span>
                  )}
                </div>
                <div className="nfe-edit-input-wrapper">
                  <Table size={18} className="nfe-edit-input-icon" />
                  <input 
                    type="text"
                    className="nfe-edit-input"
                    value={selectedTable}
                    readOnly
                    onClick={() => !isReadOnly && setTableModalOpen(true)}
                    style={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                    placeholder="Selecione uma tabela..."
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="nfe-edit-grid">

        {/* Coluna Direita: Card de Produtos */}
        <div className="nfe-edit-products-card">
          {/* Busca de produtos com ERPProductSearch */}
          <div className="nfe-edit-products-search">
            <ERPProductSearch 
              onSelect={handleSelectProduct}
              placeholder="Buscar produto por nome, SKU, código de barras ou marca..."
            />
          </div>

          {/* Formulário de adicionar item */}
          {selectedProduct && (
            <div className="nfe-edit-item-form">
              <div className="nfe-edit-item-form-row">
                <div className="nfe-edit-field product-field">
                  <label className="nfe-edit-label">Produto</label>
                  <div className="nfe-edit-selected-product">
                    <Package size={16} />
                    <span>{selectedProduct.name}</span>
                    <button
                      className="nfe-edit-btn-remove"
                      onClick={() => setSelectedProduct(null)}
                      title="Remover produto"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="nfe-edit-field quantity-field">
                  <label className="nfe-edit-label">Quantidade</label>
                  <input
                    id="quantity-input"
                    type="text"
                    className="nfe-edit-input"
                    value={quantity}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d,]/g, '')
                      setQuantity(value)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddItem()
                      }
                    }}
                    disabled={isReadOnly}
                    placeholder="1,000"
                  />
                </div>
                
                <div className="nfe-edit-field price-field">
                  <label className="nfe-edit-label">Preço Unit.</label>
                  <input
                    type="text"
                    className="nfe-edit-input"
                    value={unitPrice}
                    readOnly
                    disabled={isReadOnly}
                    placeholder="0,00"
                    style={{ cursor: 'not-allowed', opacity: 0.7 }}
                  />
                </div>
                
                <div className="nfe-edit-field discount-field">
                  <label className="nfe-edit-label">Desconto (%)</label>
                  <input
                    type="text"
                    className="nfe-edit-input"
                    value={discountPercent}
                    onChange={(e) => {
                      // Verificar se produto está na tabela
                      if (selectedProduct && productInTable[selectedProduct.id]) {
                        showToast('Produtos na tabela de preços não podem ter desconto', 'warning')
                        return
                      }
                      const value = e.target.value.replace(/[^\d,]/g, '')
                      setDiscountPercent(value)
                    }}
                    disabled={isReadOnly || (selectedProduct && productInTable[selectedProduct.id])}
                    placeholder="0,00"
                    style={{ 
                      cursor: (selectedProduct && productInTable[selectedProduct.id]) ? 'not-allowed' : 'text',
                      opacity: (selectedProduct && productInTable[selectedProduct.id]) ? 0.7 : 1
                    }}
                    title={(selectedProduct && productInTable[selectedProduct.id]) ? 'Produtos na tabela de preços não podem ter desconto' : ''}
                  />
                </div>
                
                <div className="nfe-edit-field add-button-field">
                  <label style={{ visibility: 'hidden' }}>Ação</label>
                  <button
                    className="nfe-edit-btn-add"
                    onClick={handleAddItem}
                    disabled={isReadOnly}
                    title="Adicionar produto à NF-e"
                  >
                    <Plus size={18} />
                    Adicionar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Lista de Itens */}
          <div className="nfe-edit-items-container">
            {items.length > 0 ? (
              <table className="nfe-edit-items-table">
                <thead>
                  <tr>
                    <th>Item / Catálogo</th>
                    <th className="nfe-edit-th-center">Quantidade</th>
                    <th className="nfe-edit-th-right">Preço Unit.</th>
                    <th className="nfe-edit-th-right">Desconto</th>
                    <th className="nfe-edit-th-right">Subtotal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id || `sup-${item.product_id}`} className="nfe-edit-item-row">
                      <td>
                        <div className="nfe-edit-item-product">
                          <div className="nfe-edit-item-image">
                            <Package size={20} />
                          </div>
                          <div className="nfe-edit-item-info">
                            <p className="nfe-edit-item-name">
                              {item.product?.name || `Produto #${item.product_id}`}
                              {item.is_supplemental && (
                                <span className="nfe-edit-item-supplemental-badge" title="Item da venda do caixa — não altera estoque">
                                  Suplementar
                                </span>
                              )}
                            </p>
                            {item.product?.sku && (
                              <p className="nfe-edit-item-sku">{item.product.sku}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="nfe-edit-table-input"
                          value={item.quantity.toFixed(3).replace('.', ',')}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d,]/g, '')
                            handleUpdateItem(item.id, 'quantity', value)
                          }}
                          disabled={isReadOnly || item.is_supplemental}
                          readOnly={item.is_supplemental}
                          title={item.is_supplemental ? 'Quantidade espelhada da venda — não editável' : ''}
                        />
                      </td>
                      <td className="nfe-edit-td-right">
                        <input
                          type="text"
                          className="nfe-edit-table-input"
                          value={item.unit_price.toFixed(2).replace('.', ',')}
                          readOnly
                          disabled={isReadOnly}
                          style={{ cursor: 'not-allowed', opacity: 0.7 }}
                        />
                      </td>
                      <td className="nfe-edit-td-right">
                        <input
                          type="text"
                          className="nfe-edit-table-input"
                          value={item.discount_percent.toFixed(2).replace('.', ',')}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d,]/g, '')
                            handleUpdateItem(item.id, 'discount_percent', value)
                          }}
                          disabled={isReadOnly || item.is_supplemental || productInTable[item.product_id]}
                          style={{ 
                            cursor: productInTable[item.product_id] ? 'not-allowed' : 'text',
                            opacity: productInTable[item.product_id] ? 0.7 : 1
                          }}
                          title={productInTable[item.product_id] ? 'Produtos na tabela de preços não podem ter desconto' : ''}
                        />
                      </td>
                      <td className="nfe-edit-td-right">
                        <span className="nfe-edit-item-total">{formatCurrency(item.total_price)}</span>
                      </td>
                      <td className="nfe-edit-td-right">
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="nfe-edit-item-remove"
                          disabled={isReadOnly || item.is_supplemental}
                          title={item.is_supplemental ? 'Itens suplementares da venda não podem ser removidos' : ''}
                        >
                          <Trash2 size={20} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="nfe-edit-items-empty">
                <Package size={48} />
                <p className="nfe-edit-items-empty-text">Nenhum item adicionado à NF-e</p>
              </div>
            )}
          </div>

          {/* Totais */}
          <div className="nfe-edit-totals">
            <div className="nfe-edit-totals-left">
              <div className="nfe-edit-total-item">
                <p className="nfe-edit-total-label">Subtotal Bruto</p>
                <p className="nfe-edit-total-value">{formatCurrency(subtotal)}</p>
              </div>
              <div className="nfe-edit-total-item">
                <p className="nfe-edit-total-label-discount">Desconto Comercial</p>
                <p className="nfe-edit-total-value-discount">- {formatCurrency(totalDiscount)}</p>
              </div>
            </div>
            <div className="nfe-edit-totals-right">
              <p className="nfe-edit-total-label-final">Total Final</p>
              <p className="nfe-edit-total-value-final">{formatCurrency(finalTotal)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Informações Adicionais */}
      <div className="nfe-edit-additional-info">
        <div className="nfe-edit-additional-header" onClick={() => setIsAdditionalInfoExpanded(!isAdditionalInfoExpanded)}>
          <h2 className="nfe-edit-additional-title">Informações Adicionais</h2>
          <button
            type="button"
            className="nfe-edit-additional-toggle"
            aria-label={isAdditionalInfoExpanded ? 'Minimizar' : 'Expandir'}
          >
            {isAdditionalInfoExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
        {isAdditionalInfoExpanded && (
          <div className="nfe-edit-additional-content">
            <div className="nfe-edit-additional-field">
              <label className="nfe-edit-label">Observações</label>
              <textarea
                className="nfe-edit-textarea"
                value={formData.observation}
                onChange={(e) => setFormData({ ...formData, observation: e.target.value })}
                rows="3"
                placeholder="Observações sobre a NF-e..."
                disabled={isReadOnly}
              />
            </div>
          </div>
        )}
      </div>

      {/* Botões de ação */}
      <div className="nfe-edit-actions">
        <div className="form-actions">
          <div className="form-actions-left">
            <button
              className="btn-secondary"
              onClick={() => navigate('/erp/vendas/nfe')}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              className="btn-secondary"
              onClick={handleSave}
              disabled={saving || isReadOnly}
            >
              <Save size={18} />
              {saving ? 'Salvando...' : 'Salvar como Rascunho'}
            </button>
          </div>
          <div className="form-actions-right">
            <button
              className="nfe-edit-btn-save"
              onClick={handleFinalize}
              disabled={saving || isReadOnly || !formData.customerId || items.length === 0 || nfe?.status === 'transmitted' || nfe?.status === 'cancelled'}
              title={nfe?.status === 'transmitted' ? 'NF-e já foi transmitida' : nfe?.status === 'cancelled' ? 'NF-e foi cancelada' : isReadOnly ? 'NF-e finalizada - somente visualização' : ''}
            >
              {saving ? <Loader2 size={18} className="spinner" /> : <CheckCircle size={18} />}
              {saving ? 'Finalizando...' : 'Finalizar Venda'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      {/* Modal de Seleção de Tabela de Preços */}
      {tableModalOpen && (
        <div 
          className="nfe-customer-modal-overlay" 
          onClick={() => setTableModalOpen(false)}
        >
          <div 
            className="nfe-customer-modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nfe-customer-modal-header">
              <div className="nfe-customer-modal-header-left">
                <div className="nfe-customer-modal-icon">
                  <Table size={24} />
                </div>
                <div>
                  <h2 className="nfe-customer-modal-title">Selecionar Tabela de Preços</h2>
                  <p className="nfe-customer-modal-subtitle">Escolha a tabela de preços para esta NF-e</p>
                </div>
              </div>
              <button 
                className="nfe-customer-modal-close"
                onClick={() => setTableModalOpen(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="nfe-customer-modal-body">
              <div className="nfe-customer-search-box">
                <Search size={18} className="nfe-customer-search-icon" />
                <input
                  ref={(el) => {
                    if (el && tableModalOpen) {
                      setTimeout(() => el.focus(), 100)
                    }
                  }}
                  type="text"
                  className="nfe-customer-search-input"
                  placeholder="Buscar tabela por nome..."
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (!tableModalOpen) return
                    switch (e.key) {
                      case 'ArrowDown':
                        e.preventDefault()
                        setSelectedTableIndex(prev => 
                          prev < filteredTables.length - 1 ? prev + 1 : prev
                        )
                        break
                      case 'ArrowUp':
                        e.preventDefault()
                        setSelectedTableIndex(prev => prev > 0 ? prev - 1 : -1)
                        break
                      case 'Enter':
                        e.preventDefault()
                        if (selectedTableIndex >= 0 && selectedTableIndex < filteredTables.length) {
                          handleSelectTable(filteredTables[selectedTableIndex])
                        }
                        break
                      case 'Escape':
                        setTableModalOpen(false)
                        break
                      default:
                        break
                    }
                  }}
                />
                {loadingTables && (
                  <div className="nfe-customer-search-loading">
                    <Loader2 size={16} className="spinning" />
                  </div>
                )}
              </div>

              <div className="nfe-customer-list">
                {loadingTables ? (
                  <div className="nfe-customer-empty">
                    <Loader2 size={32} className="spinning" />
                    <p>Carregando tabelas...</p>
                  </div>
                ) : filteredTables.length > 0 ? (
                  filteredTables.map((table, index) => (
                    <div
                      key={table.id || 'default'}
                      className={`nfe-customer-item ${selectedTableIndex === index ? 'selected' : ''} ${selectedTableId === table.id ? 'active' : ''}`}
                      onClick={() => handleSelectTable(table)}
                    >
                      <div className="nfe-customer-item-avatar">
                        <Table size={20} />
                      </div>
                      <div className="nfe-customer-item-info">
                        <div className="nfe-customer-item-name">{table.name}</div>
                        <div className="nfe-customer-item-details">
                          {table.id ? (
                            <span className="nfe-customer-item-detail">Tabela Customizada</span>
                          ) : (
                            <span className="nfe-customer-item-detail">Tabela Padrão</span>
                          )}
                        </div>
                      </div>
                      {selectedTableId === table.id && (
                        <CheckCircle size={20} className="nfe-customer-item-check" />
                      )}
                    </div>
                  ))
                ) : (
                  <div className="nfe-customer-empty">
                    <Table size={48} />
                    <p>{tableSearch.trim() ? 'Nenhuma tabela encontrada' : 'Digite para buscar tabelas'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Cliente Próprio */}
      {customerModalOpen && (
        <div 
          className="nfe-customer-modal-overlay" 
          onClick={() => setCustomerModalOpen(false)}
        >
          <div 
            className="nfe-customer-modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nfe-customer-modal-header">
              <div className="nfe-customer-modal-header-left">
                <div className="nfe-customer-modal-icon">
                  <Users size={24} />
                </div>
                <div>
                  <h2 className="nfe-customer-modal-title">Selecionar Cliente</h2>
                  <p className="nfe-customer-modal-subtitle">Busque e selecione o cliente da NF-e</p>
                </div>
              </div>
              <button 
                className="nfe-customer-modal-close"
                onClick={() => setCustomerModalOpen(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="nfe-customer-modal-body">
              <div className="nfe-customer-search-box">
                <Search size={18} className="nfe-customer-search-icon" />
                <input
                  ref={(el) => {
                    if (el && customerModalOpen) {
                      setTimeout(() => el.focus(), 100)
                    }
                  }}
                  type="text"
                  className="nfe-customer-search-input"
                  placeholder="Buscar por nome, CPF/CNPJ, email ou telefone..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (!customerModalOpen) return
                    switch (e.key) {
                      case 'ArrowDown':
                        e.preventDefault()
                        setSelectedCustomerIndex(prev => 
                          prev < filteredCustomers.length - 1 ? prev + 1 : prev
                        )
                        break
                      case 'ArrowUp':
                        e.preventDefault()
                        setSelectedCustomerIndex(prev => prev > 0 ? prev - 1 : -1)
                        break
                      case 'Enter':
                        e.preventDefault()
                        if (selectedCustomerIndex >= 0 && selectedCustomerIndex < filteredCustomers.length) {
                          handleSelectCustomer(filteredCustomers[selectedCustomerIndex])
                        }
                        break
                      case 'Escape':
                        setCustomerModalOpen(false)
                        break
                      default:
                        break
                    }
                  }}
                />
                {loadingCustomers && (
                  <div className="nfe-customer-search-loading">
                    <Loader2 size={16} className="spinning" />
                  </div>
                )}
              </div>

              <div className="nfe-customer-list">
                {loadingCustomers ? (
                  <div className="nfe-customer-empty">
                    <Loader2 size={32} className="spinning" />
                    <p>Carregando clientes...</p>
                  </div>
                ) : filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer, index) => (
                    <div
                      key={customer.id}
                      className={`nfe-customer-item ${selectedCustomerIndex === index ? 'selected' : ''} ${formData.customerId === customer.id ? 'active' : ''}`}
                      onClick={() => handleSelectCustomer(customer)}
                    >
                      <div className="nfe-customer-item-avatar">
                        <Users size={20} />
                      </div>
                      <div className="nfe-customer-item-info">
                        <div className="nfe-customer-item-name">{customer.name}</div>
                        <div className="nfe-customer-item-details">
                          {customer.cpf_cnpj && (
                            <span className="nfe-customer-item-detail">
                              {customer.cpf_cnpj.length === 11 
                                ? `CPF: ${customer.cpf_cnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}`
                                : `CNPJ: ${customer.cpf_cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}`
                              }
                            </span>
                          )}
                          {customer.email && (
                            <span className="nfe-customer-item-detail">{customer.email}</span>
                          )}
                        </div>
                      </div>
                      {formData.customerId === customer.id && (
                        <CheckCircle size={20} className="nfe-customer-item-check" />
                      )}
                    </div>
                  ))
                ) : (
                  <div className="nfe-customer-empty">
                    <Users size={48} />
                    <p>{customerSearchTerm.trim() ? 'Nenhum cliente encontrado' : 'Digite para buscar clientes'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pagamento Próprio */}
      {paymentModalOpen && (
        <div 
          className="nfe-payment-modal-overlay" 
          onClick={() => setPaymentModalOpen(false)}
        >
          <div 
            className="nfe-payment-modal-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nfe-payment-modal-header">
              <div className="nfe-payment-modal-header-left">
                <div className="nfe-payment-modal-icon">
                  <CheckCircle size={24} />
                </div>
                <div>
                  <h2 className="nfe-payment-modal-title">Formas de Pagamento</h2>
                  <p className="nfe-payment-modal-subtitle">Configure os pagamentos da NF-e</p>
                </div>
              </div>
              <button 
                className="nfe-payment-modal-close"
                onClick={() => setPaymentModalOpen(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="nfe-payment-modal-body">
              {paymentError && (
                <div className="nfe-payment-error">
                  <X size={18} />
                  <span>{paymentError}</span>
                </div>
              )}

              <div className="nfe-payment-summary">
                <div className="nfe-payment-summary-item">
                  <span>Valor da NF-e:</span>
                  <strong>{formatCurrency(totals.finalAmount)}</strong>
                </div>
                {formData.customerId && customerBonusBalance && (
                  <div className="nfe-payment-summary-item" style={{ 
                    backgroundColor: '#f0fdf4', 
                    padding: '0.75rem', 
                    borderRadius: '8px',
                    border: '1px solid #86efac'
                  }}>
                    <span>Saldo de Bônus Disponível:</span>
                    <strong style={{ color: '#059669' }}>
                      {formatCurrency(parseFloat(customerBonusBalance.availableBalance || 0))}
                    </strong>
                  </div>
                )}
                <div className="nfe-payment-summary-item">
                  <span>Total dos Pagamentos:</span>
                  <strong>{formatCurrency(calculatePaymentTotal())}</strong>
                </div>
                <div className={`nfe-payment-summary-item ${Math.abs(calculatePaymentTotal() - totals.finalAmount) <= 0.01 ? 'exact' : 'difference'}`}>
                  <span>{Math.abs(calculatePaymentTotal() - totals.finalAmount) <= 0.01 ? '✓ Valor correto' : 'Diferença:'}</span>
                  <strong>{formatCurrency(Math.abs(calculatePaymentTotal() - totals.finalAmount))}</strong>
                </div>
              </div>

              {/* Formas selecionadas */}
              <div className="nfe-payment-selected">
                <h3 className="nfe-payment-section-title">Formas Selecionadas</h3>
                {selectedPayments.length === 0 ? (
                  <p className="nfe-payment-empty">Nenhuma forma de pagamento selecionada</p>
                ) : (
                  <div className="nfe-payment-list">
                    {selectedPayments.map((payment) => (
                      <div key={payment.id} className="nfe-payment-item">
                        <div className="nfe-payment-item-header">
                          <div className="nfe-payment-item-info">
                            <span className="nfe-payment-item-name">{payment.methodName}</span>
                            {payment.isCrediario && (
                              <span className="nfe-payment-crediario-badge">Crediário</span>
                            )}
                            {payment.isBonus && (
                              <span className="nfe-payment-crediario-badge" style={{ backgroundColor: '#d1fae5', color: '#059669' }}>
                                Bônus
                              </span>
                            )}
                          </div>
                          <button
                            className="nfe-payment-item-remove"
                            onClick={() => handleRemovePayment(payment.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="nfe-payment-item-fields">
                          {payment.isBonus && customerBonusBalance && (
                            <div className="nfe-payment-field" style={{ 
                              marginBottom: '0.5rem',
                              padding: '0.5rem',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '6px',
                              fontSize: '0.875rem'
                            }}>
                              <span style={{ color: '#059669', fontWeight: '500' }}>
                                Saldo disponível: {formatCurrency(parseFloat(customerBonusBalance.availableBalance || 0))}
                              </span>
                            </div>
                          )}
                          <div className="nfe-payment-field">
                            <label>Valor</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={payment.isBonus && customerBonusBalance ? customerBonusBalance.availableBalance : undefined}
                              value={payment.amount || ''}
                              onChange={(e) => handleUpdatePaymentAmount(payment.id, e.target.value)}
                              placeholder="0.00"
                            />
                            {payment.isBonus && customerBonusBalance && (
                              <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                                Máximo: {formatCurrency(parseFloat(customerBonusBalance.availableBalance || 0))}
                              </small>
                            )}
                          </div>

                          {payment.isCrediario && (
                            <>
                              <div className="nfe-payment-field">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                  <label style={{ margin: 0, flex: 1 }}>Prazo de Pagamento *</label>
                                  <button
                                    type="button"
                                    onClick={() => setShowTermModal(true)}
                                    className="nfe-payment-new-term-btn"
                                  >
                                    <Plus size={14} />
                                    Novo Prazo
                                  </button>
                                </div>
                                {loadingPayments ? (
                                  <select disabled>
                                    <option>Carregando prazos...</option>
                                  </select>
                                ) : paymentTerms.length === 0 ? (
                                  <div className="nfe-payment-warning">
                                    ⚠️ Nenhum prazo cadastrado. Clique em "Novo Prazo" para cadastrar.
                                  </div>
                                ) : (
                                  <select
                                    value={payment.paymentTermId || ''}
                                    onChange={(e) => handleUpdatePaymentTerm(payment.id, e.target.value)}
                                  >
                                    <option value="">Selecione um prazo</option>
                                    {paymentTerms.map(term => (
                                      <option key={term.id} value={term.id}>
                                        {term.name} ({term.installments}x)
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>

                              {payment.installments && payment.installments.length > 0 && (
                                <div className="nfe-payment-installments">
                                  <label>Parcelas e Vencimentos</label>
                                  <div className="nfe-payment-installments-list">
                                    {payment.installments.map((inst) => (
                                      <div key={inst.installmentNumber} className="nfe-payment-installment-item">
                                        <span className="nfe-payment-installment-number">
                                          Parcela {inst.installmentNumber}
                                        </span>
                                        <div className="nfe-payment-installment-details">
                                          <input
                                            type="date"
                                            value={inst.dueDate}
                                            onChange={(e) => handleUpdateInstallmentDate(
                                              payment.id,
                                              inst.installmentNumber,
                                              e.target.value
                                            )}
                                          />
                                          <span className="nfe-payment-installment-amount">
                                            {formatCurrency(inst.amount)}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Formas disponíveis */}
              <div className="nfe-payment-available">
                <h3 className="nfe-payment-section-title">Adicionar Forma de Pagamento</h3>
                {loadingPayments ? (
                  <p>Carregando...</p>
                ) : (
                  <div className="nfe-payment-methods-grid">
                    {paymentMethods.map((method) => {
                      const isSelected = selectedPayments.some(
                        p => p.paymentReceiptMethodId === method.id
                      )
                      
                      const isCrediario = method.name?.toLowerCase().includes('crediário') || 
                                         method.name?.toLowerCase().includes('crediario') ||
                                         method.code === 'CREDIARIO' ||
                                         method.payment_type === 'CREDIÁRIO' ||
                                         method.payment_type === 'CREDIARIO'

                      return (
                        <button
                          key={method.id}
                          className={`nfe-payment-method-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => !isSelected && handleAddPayment(method)}
                          disabled={isSelected}
                        >
                          {isCrediario ? (
                            <CheckCircle size={24} />
                          ) : (
                            <CheckCircle size={24} />
                          )}
                          <span>{method.name}</span>
                        </button>
                      )
                    })}
                    
                    {/* Botão de Bônus - apenas se houver cliente e saldo disponível */}
                    {formData.customerId && customerBonusBalance && parseFloat(customerBonusBalance.availableBalance || 0) > 0 && (
                      <button
                        className={`nfe-payment-method-card ${selectedPayments.some(p => p.isBonus) ? 'selected' : ''}`}
                        onClick={() => !selectedPayments.some(p => p.isBonus) && handleAddBonusPayment()}
                        disabled={selectedPayments.some(p => p.isBonus)}
                        style={{
                          backgroundColor: selectedPayments.some(p => p.isBonus) ? undefined : '#f0fdf4',
                          borderColor: '#86efac'
                        }}
                      >
                        <Gift size={24} style={{ color: '#059669' }} />
                        <span style={{ color: '#059669', fontWeight: '600' }}>
                          Bônus ({formatCurrency(parseFloat(customerBonusBalance.availableBalance || 0))})
                        </span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="nfe-payment-modal-footer">
              <button 
                className="nfe-payment-btn-secondary" 
                onClick={() => setPaymentModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                className="nfe-payment-btn-primary"
                onClick={handleConfirmPayments}
                disabled={Math.abs(calculatePaymentTotal() - totals.finalAmount) > 0.01 || selectedPayments.length === 0}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de cadastro de prazo */}
      {showTermModal && (
        <div 
          className="nfe-payment-modal-overlay" 
          onClick={() => !savingTerm && setShowTermModal(false)}
        >
          <div 
            className="nfe-payment-modal-content" 
            style={{ maxWidth: '500px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nfe-payment-modal-header">
              <div className="nfe-payment-modal-header-left">
                <div className="nfe-payment-modal-icon">
                  <Plus size={24} />
                </div>
                <div>
                  <h2 className="nfe-payment-modal-title">Cadastrar Prazo de Pagamento</h2>
                </div>
              </div>
              <button 
                className="nfe-payment-modal-close"
                onClick={() => !savingTerm && setShowTermModal(false)}
                disabled={savingTerm}
              >
                <X size={24} />
              </button>
            </div>

            <div className="nfe-payment-modal-body">
              {termErrors.submit && (
                <div className="nfe-payment-error" style={{ marginBottom: '1rem' }}>
                  <X size={18} />
                  <span>{termErrors.submit}</span>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="nfe-payment-field">
                  <label>Nome do Prazo *</label>
                  <input
                    type="text"
                    value={termFormData.name}
                    onChange={(e) => setTermFormData({ ...termFormData, name: e.target.value })}
                    placeholder="Ex: 30/60/90 dias"
                    disabled={savingTerm}
                  />
                  {termErrors.name && (
                    <small className="nfe-payment-field-error">{termErrors.name}</small>
                  )}
                </div>

                <div className="nfe-payment-field">
                  <label>Código (opcional)</label>
                  <input
                    type="text"
                    value={termFormData.code}
                    onChange={(e) => setTermFormData({ ...termFormData, code: e.target.value })}
                    placeholder="Ex: 30-60-90"
                    disabled={savingTerm}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="nfe-payment-field">
                    <label>Número de Parcelas *</label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={termFormData.installments}
                      onChange={(e) => setTermFormData({ ...termFormData, installments: parseInt(e.target.value) || 1 })}
                      disabled={savingTerm}
                    />
                    {termErrors.installments && (
                      <small className="nfe-payment-field-error">{termErrors.installments}</small>
                    )}
                  </div>

                  <div className="nfe-payment-field">
                    <label>Dias até 1ª Parcela *</label>
                    <input
                      type="number"
                      min="0"
                      value={termFormData.daysUntilFirst}
                      onChange={(e) => setTermFormData({ ...termFormData, daysUntilFirst: parseInt(e.target.value) || 0 })}
                      disabled={savingTerm}
                    />
                    {termErrors.daysUntilFirst && (
                      <small className="nfe-payment-field-error">{termErrors.daysUntilFirst}</small>
                    )}
                  </div>
                </div>

                <div className="nfe-payment-field">
                  <label>Dias entre Parcelas *</label>
                  <input
                    type="number"
                    min="0"
                    value={termFormData.daysBetweenInstallments}
                    onChange={(e) => setTermFormData({ ...termFormData, daysBetweenInstallments: parseInt(e.target.value) || 0 })}
                    placeholder="Ex: 30 para parcelas mensais"
                    disabled={savingTerm}
                  />
                  {termErrors.daysBetweenInstallments && (
                    <small className="nfe-payment-field-error">{termErrors.daysBetweenInstallments}</small>
                  )}
                  <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                    Ex: 30 = parcelas mensais, 15 = parcelas quinzenais
                  </small>
                </div>

                <div className="nfe-payment-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={termFormData.active}
                      onChange={(e) => setTermFormData({ ...termFormData, active: e.target.checked })}
                      disabled={savingTerm}
                    />
                    <span>Ativo</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="nfe-payment-modal-footer">
              <button 
                className="nfe-payment-btn-secondary" 
                onClick={() => setShowTermModal(false)}
                disabled={savingTerm}
              >
                Cancelar
              </button>
              <button
                className="nfe-payment-btn-primary"
                onClick={handleCreateTerm}
                disabled={savingTerm}
              >
                {savingTerm ? 'Salvando...' : 'Salvar Prazo'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />
    </div>
  )
}

export default NFeEditPage
